import { apiClient } from '@/shared/api';
import { API_ENDPOINTS } from '@/config';
import { buildCommonHeaders } from '@/shared/http/commonHeaders';
import type { GenericResponse } from '@/features/card/types/card.types';
import type { AddUserFormValues, ApproveUserFormValues, EditUserFormValues, UserDetails, InvitationAceptToken } from '@/features/team/types/team.types';
import type { ExportDirective } from '@/shared/export/types';


class TeamService {
	async getListMembers(page: number, limit: number, filters: Record<string, string> = {}): Promise<GenericResponse> {
		const newHeaders = buildCommonHeaders({
			withLocation: true
		});

		const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.team.listMembers, { data: { filter: { page_number: page, results_per_page: limit, ...filters } } }, { headers: newHeaders, params: { order: "desc" } });
		return response.data;
	}

	/**
	 * Downloads the team report as a file (BF-613): same list command plus an
	 * `export` directive, answered with the rendered PDF.
	 */
	async exportTeam(filters: Record<string, string | number>, directive: ExportDirective) {
		const newHeaders = buildCommonHeaders({ withLocation: true });

		return apiClient.download(API_ENDPOINTS.team.listMembers, {
			body: { data: { filter: filters, export: directive } },
			headers: newHeaders,
			params: { order: 'desc' },
		});
	}

	async createMember(values: AddUserFormValues): Promise<GenericResponse> {
		const newHeaders = buildCommonHeaders({
			withLocation: true
		});

		const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.team.createMember, { data: values }, { headers: newHeaders });
		return response.data.data;
	}


	async getMember(member_uuid: string): Promise<UserDetails> {
		const newHeaders = buildCommonHeaders({
			withLocation: true
		});

		const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.team.getMember, {}, { headers: newHeaders, params: member_uuid });
		return response.data.data;
	}

	async inviteAccept(values: InvitationAceptToken): Promise<GenericResponse> {
		const newHeaders = buildCommonHeaders({
			withLocation: true
		});

		const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.team.inviteAccept, { data: values }, { headers: newHeaders });
		return response.data.data;
	}

	async approveMember(values: ApproveUserFormValues, member_uuid: string): Promise<GenericResponse> {
		const newHeaders = buildCommonHeaders({
			withLocation: true
		});

		const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.team.approveMember, { data: values }, { headers: newHeaders, params: member_uuid });
		return response.data.data;
	}

	async updateMember(values: EditUserFormValues, member_uuid: string): Promise<GenericResponse> {
		const newHeaders = buildCommonHeaders({
			withLocation: true
		});

		const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.team.updateMember, { data: values }, { headers: newHeaders, params: member_uuid });
		return response.data.data;
	}

	async resendInvitation(userUuid: string): Promise<GenericResponse> {
		const newHeaders = buildCommonHeaders({
			withLocation: true
		});

		const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.team.resendInvitation, {}, { headers: newHeaders, params: userUuid });
		return response.data.data;
	}

	async cancelMember(member_uuid: string): Promise<GenericResponse> {
		const newHeaders = buildCommonHeaders({
			withLocation: true
		});

		const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.team.cancelMember, {}, { headers: newHeaders, params: member_uuid });
		return response.data.data;
	}
}

export const teamService = new TeamService();
